import { useState } from "react";
import { useNavigate } from "react-router";
import { Lock, Mail } from "lucide-react";
import { formStateFor } from "@/modules/identity/commands/authCopy";
import type { Failure } from "@/modules/shared/failure";
import { Banner, Field } from "@/design/ui";
import { AuthShell } from "~/components/AuthShell";
import { AuthHeading, SubmitButton } from "~/components/forms";
import { useMe } from "~/auth/MeProvider";
import { adoptSession } from "~/auth/supabase";
import { post } from "~/lib/api";
import { useForm } from "~/lib/useForm";

/**
 * AdminLogInScreen (docs/ux/screens/AdminLogInScreen.md).
 *
 * **Its own screen rather than a branch on the staff form.** Admins are Bookr
 * staff, not clinics: they are not customers of the phone-identity rules that
 * exist so a practitioner can also be somebody's patient, and they are the
 * smallest audience in the app. Keeping them on the business form is what made
 * "the business side logs in by phone" ambiguous in the first place.
 *
 * Not linked from anywhere. There is no reason to advertise it, and the people
 * who need it know the URL.
 */
interface AdminLogInResult {
  session: { accessToken: string; refreshToken: string };
}

/**
 * The one screen where the identifier is an email, so the one refusal that does
 * not say "Phone or password". Same code, said for what was actually typed.
 */
function adminStateFor(failure: Failure, values: Record<string, string>) {
  if (failure.code === "credentials_incorrect") {
    return { formError: "Email or password is incorrect.", values };
  }
  return formStateFor(failure, values);
}

export function AdminLogInScreen() {
  const navigate = useNavigate();
  const { reload } = useMe();
  const { state, pending, submit } = useForm();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  async function onSubmit(event: React.FormEvent) {
    event.preventDefault();
    const result = await submit<AdminLogInResult>(
      () => post("/auth/admin/login", { email, password }),
      adminStateFor,
      { email },
    );
    if (!result) return;

    await adoptSession(result.session);
    await reload();
    void navigate("/admin", { replace: true });
  }

  return (
    <AuthShell>
      <AuthHeading title="Bookr admin" sub="Platform administration" />

      {state.formError && <Banner>{state.formError}</Banner>}

      <form onSubmit={onSubmit}>
        <fieldset disabled={pending} className="disabled:opacity-70">
          <Field
            label="Email"
            name="email"
            type="email"
            value={email}
            onChange={setEmail}
            error={state.fieldErrors?.email}
            autoComplete="email"
            icon={Mail}
          />
          <Field
            label="Password"
            name="password"
            type="password"
            value={password}
            onChange={setPassword}
            error={state.fieldErrors?.password}
            autoComplete="current-password"
            icon={Lock}
          />
          <SubmitButton pending={pending} pendingLabel="Logging in…">
            Log in
          </SubmitButton>
        </fieldset>
      </form>
    </AuthShell>
  );
}
